import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, Navigate, useLocation, Outlet } from "react-router-dom";
import Footer from "./Components/Layout/Footer";
import SideBar from "./Components/Layout/SideBar";
import ProtectedRoute from "./Components/ProtectedRoute";
import { MenuOutlined } from "@ant-design/icons";
import { ConfigProvider, theme } from "antd";
import i18n from "./i18n";
import tr_TR from "antd/lib/locale/tr_TR";
import en_US from "antd/lib/locale/en_US";
import moment from "moment";
import "moment/locale/tr";
import "moment/locale/en-gb";
import "moment/locale/en-ca";
import { Loader } from "./Components/Loader";
import { Toaster } from 'react-hot-toast';
import { SignalRProvider } from "./Context/ChatContext";
import SignalRMonitor from "./Components/SignalRMonitor";
import AppInitializer from "./Components/AppInitializer";
import MobileErrorBoundary from "./Components/MobileErrorBoundary";
import ROUTES from './routes';
import './assets/css/layout-overrides.css';


// Lazy load components - sık kullanılanlar prefetch ile önden yüklenir
const Login = lazy(() => import("./Pages/Login"));
const Dashboard = lazy(() => import(/* webpackPrefetch: true */ "./Pages/Dashboard"));
const SmartMap = lazy(() => import("./Pages/SmartMap"));
const Region = lazy(() => import("./Pages/Region"));
const Users = lazy(() => import(/* webpackPrefetch: true */ "./Pages/Users"));
const UserDetail = lazy(() => import("./Pages/UserDetail"));
const Trips = lazy(() => import(/* webpackPrefetch: true */ "./Pages/Trips"));
const TripDetail = lazy(() => import("./Pages/TripDetail"));
const LiveRides = lazy(() => import("./Pages/LiveRides"));
const NotFoundPage = lazy(() => import("./Pages/NotFound"));
const DistanceDiscounts = lazy(() => import("./Pages/DistanceDiscounts"));
const VehicleTypes = lazy(() => import("./Pages/VehicleTypes"));
const Drivers = lazy(() => import(/* webpackPrefetch: true */ "./Pages/Drivers"));
const Settings = lazy(() => import("./Pages/Settings"));
const Faq = lazy(() => import("./Pages/Faq"));
const Pages = lazy(() => import("./Pages/Pages"));
const BankAccounts = lazy(() => import("./Pages/BankAccounts"));
const NotificationSend = lazy(() => import("./Pages/NotificationSend"));
const ChatApp = lazy(() => import("./Pages/ChatApp"));
const Profile = lazy(() => import("./Pages/Profile"));
const SmsLogs = lazy(() => import("./Pages/SmsLogs"));
const ActiveDrivers = lazy(() => import("./Pages/ActiveDrivers"));
const AuthorizedUsers = lazy(() => import("./Pages/AuthorizedUsers"));
const Agreements = lazy(() => import("./Pages/Agreements"));
const MobileDashboard = lazy(() => import("./Pages/Mobile/MobileDashboard"));
const MobileSmartMap = lazy(() => import("./Pages/Mobile/MobileSmartMap"));
const MobileUsers = lazy(() => import("./Pages/Mobile/MobileUsers"));
const MobileDrivers = lazy(() => import("./Pages/Mobile/MobileDrivers"));
const MobileTrips = lazy(() => import("./Pages/Mobile/MobileTrips"));
const MobileLiveRides = lazy(() => import("./Pages/Mobile/MobileLiveRides"));
const MobileNotifications = lazy(() => import("./Pages/Mobile/MobileNotifications"));
const SignalRTest = lazy(() => import("./Components/SignalRTest"));
const SocketServiceTest = lazy(() => import("./Components/SocketServiceTest"));
const BulkSms = lazy(() => import("./Pages/BulkSms"));
const MetricsOverview = lazy(() => import("./Pages/Metrics/Overview"));
const DriverMetrics = lazy(() => import("./Pages/Metrics/DriverMetrics"));
const PassengerMetrics = lazy(() => import("./Pages/Metrics/PassengerMetrics"));
const PerformanceReport = lazy(() => import("./Pages/Metrics/PerformanceReport"));
const CoverageHeatmap = lazy(() => import("./Pages/Metrics/CoverageHeatmap"));
const MobileMetricsOverview = lazy(() => import("./Pages/Mobile/MobileMetricsOverview"));
const MobileDriverMetrics = lazy(() => import("./Pages/Mobile/MobileDriverMetrics"));
const MobilePassengerMetrics = lazy(() => import("./Pages/Mobile/MobilePassengerMetrics"));
const MobilePerformanceReport = lazy(() => import("./Pages/Mobile/MobilePerformanceReport"));
const MobileCoverageHeatmap = lazy(() => import("./Pages/Mobile/MobileCoverageHeatmap"));
const AcceptanceCancellationReport = lazy(() => import("./Pages/Metrics/AcceptanceCancellationReport"));
const DriverPenalties = lazy(() => import("./Pages/DriverPenalties"));
const MobileAcceptanceCancellationReport = lazy(() => import("./Pages/Mobile/MobileAcceptanceCancellationReport"));
const SubscriptionPlans = lazy(() => import("./Pages/SubscriptionPlans"));
const DriverDiscounts = lazy(() => import("./Pages/DriverDiscounts"));
const DriverSubscriptions = lazy(() => import("./Pages/DriverSubscriptions"));
const SubscriptionAuditLogs = lazy(() => import("./Pages/SubscriptionAuditLogs"));
const SubscriptionStatistics = lazy(() => import("./Pages/SubscriptionStatistics"));
const BankTransferPayments = lazy(() => import("./Pages/BankTransferPayments"));
const Crm = lazy(() => import("./Pages/Crm"));
const CustomerMessages = lazy(() => import("./Pages/CustomerMessages"));


// --- Layout bileşenleri (App dışında tanımlanır, her renderda yeniden oluşturulmaz) ---

const LocationListener = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const isMobile = window.innerWidth < 1200;
    if (isMobile) {
      document.body.classList.remove('g-sidenav-pinned');
    }
  }, [location.pathname]);

  return children;
};

const FloatButtonElement = () => (
  <div
    onClick={() => {
      document.body.classList.toggle("g-sidenav-pinned");
    }}
    className="sidenav-toggler sidenav-toggler-dark d-xl-none"
    id="sidenavToggler"
  >
    <MenuOutlined />
  </div>
);

// Ana desktop layout - SideBar + Footer sabit kalır, sadece içerik alanı değişir
const MainLayout = () => (
  <>
    <FloatButtonElement />
    <SideBar />
    <main className="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
      <Suspense fallback={<Loader />}>
        <Outlet />
      </Suspense>
      <Footer />
    </main>
  </>
);

// Harita sayfaları için layout - Footer yok
const MapLayout = () => (
  <>
    <FloatButtonElement />
    <SideBar />
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
  </>
);

// Mobil sayfalar için layout
const MobileRouteLayout = () => (
  <MobileErrorBoundary>
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
  </MobileErrorBoundary>
);


function App() {
  useEffect(() => {
    moment.locale(i18n.language);
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth < 1200;
    if (isMobile) {
      document.body.classList.remove('g-sidenav-pinned');
    }
  }, []);

  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AppInitializer>
        <SignalRProvider>
          <LocationListener>
            <ConfigProvider
              locale={i18n.language === "tr" ? tr_TR : en_US}
              input={{
                autoComplete: "off",
              }}
              inputNumber={{
                autoComplete: "off",
              }}
              theme={{
                token: {
                  // Typography
                  fontSize: 12,
                  fontSizeLG: 12,
                  fontSizeSM: 10,
                  
                  // Border & Radius
                  borderRadius: 6,
                  borderRadiusLG: 6,
                  borderRadiusSM: 6,
                  
                  // Primary Colors
                  colorPrimary: "#191919",
                  colorPrimaryHover: "#2d2d2d",
                  colorPrimaryActive: "#0a0a0a",
                  colorPrimaryTextActive: "#ffffff",
                  
                  // Background Colors
                  colorBgContainer: "#ffffff",
                  colorBgElevated: "#ffffff",
                  colorBgLayout: "#f5f5f5",
                  
                  // Border Colors
                  colorBorder: "#d9d9d9",
                  colorBorderSecondary: "#f0f0f0",
                  
                  // Text Colors
                  colorText: "#262626",
                  colorTextSecondary: "#8c8c8c",
                  colorTextTertiary: "#504f4f",
                  colorTextPlaceholder: "#504f4f",
                  
                  // Control
                  controlHeight: 32,
                  controlHeightLG: 40,
                  controlHeightSM: 24,
                  controlOutlineWidth: 0.5,
                  controlOutline: "#2d2d2d",
                  
                  // Shadow
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                  boxShadowSecondary: "0 1px 4px rgba(0, 0, 0, 0.08)",
                },
                algorithm: theme.defaultAlgorithm,
                components: {
                  DatePicker: {
                    fontSize: 12,
                    borderRadius: 6,
                    controlHeight: 32,
                    colorPrimary: "#191919",
                    colorPrimaryHover: "#2d2d2d",
                    colorBorder: "#d9d9d9",
                    colorBgContainer: "#ffffff",
                    colorTextPlaceholder: "#bfbfbf",
                    colorText: "#262626",
                    cellHeight: 28,
                    cellWidth: 40,
                    cellHoverBg: "#f5f5f5",
                    cellActiveWithRangeBg: "#f0f0f0",
                    controlItemBgActive: "#191919",
                    controlItemBgHover: "#fafafa",
                  },
                  Button: {
                    fontSize: 12,
                    borderRadius: 6,
                    controlHeight: 32,
                    controlHeightLG: 40,
                    controlHeightSM: 24,
                    colorPrimary: "#191919",
                    colorPrimaryHover: "#2d2d2d",
                    colorPrimaryActive: "#0a0a0a",
                    fontWeight: 500,
                    primaryShadow: "0 2px 4px rgba(25, 25, 25, 0.2)",
                  },
                  Form: {
                    labelFontSize: 12,
                    labelColor: "#262626",
                    labelRequiredMarkColor: "#ff4d4f",
                    itemMarginBottom: 16,
                    verticalLabelPadding: "0 0 4px",
                  },
                  Card: {
                    borderRadius: 6,
                    borderRadiusLG: 6,
                    colorBgContainer: "#ffffff",
                    colorBorderSecondary: "#f0f0f0",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                    headerHeight: 48,
                    headerFontSize: 14,
                    headerFontWeight: 600,
                  },
                  Statistic: {
                    titleFontSize: 12,
                    contentFontSize: 24,
                    contentFontWeight: 600,
                  },
                  Table: {
                    fontSize: 12,
                    borderRadius: 6,
                    colorBgContainer: "#ffffff",
                    headerBg: "#fafafa",
                    headerColor: "#262626",
                    rowHoverBg: "#f5f5f5",
                    cellPaddingBlock: 12,
                    cellPaddingInline: 16,
                  },
                  Input: {
                    fontSize: 12,
                    borderRadius: 6,
                    controlHeight: 32,
                    colorBorder: "#d9d9d9",
                    colorBgContainer: "#ffffff",
                    colorTextPlaceholder: "#bfbfbf",
                  },
                  Select: {
                    fontSize: 12,
                    borderRadius: 6,
                    controlHeight: 32,
                    colorBorder: "#d9d9d9",
                    colorBgContainer: "#ffffff",
                    colorTextPlaceholder: "#bfbfbf",
                    controlItemBgHover: "#f5f5f5",
                    controlItemBgActive: "#f0f0f0",
                  },
                  Tag: {
                    fontSize: 11,
                    borderRadiusSM: 6,
                  },
                  Progress: {
                    defaultColor: "#191919",
                    remainingColor: "#f0f0f0",
                  },
                },
              }}
            >
              <Toaster
                toastOptions={{
                  style: {
                    fontSize: '12px',
                    borderRadius: '10px',
                    background: '#333',
                    color: '#fff',
                  },
                  position: 'top-right',
                }}
              />

              <Routes>
                {/* Login - layout gerektirmez */}
                <Route path={ROUTES.LOGIN} element={<Suspense fallback={<Loader />}><Login /></Suspense>} />

                <Route element={<ProtectedRoute />}>
                  {/* Ana desktop layout - SideBar + Footer paylaşımlı */}
                  <Route element={<MainLayout />}>
                    <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                    <Route path={ROUTES.SETTINGS_GENERAL} element={<Settings />} />
                    <Route path={ROUTES.SETTINGS_REGION} element={<Region />} />
                    <Route path={ROUTES.SETTINGS_DISTANCE_DISCOUNTS} element={<DistanceDiscounts />} />
                    <Route path={ROUTES.SETTINGS_SUBSCRIPTION_PLANS} element={<SubscriptionPlans />} />
                    <Route path={ROUTES.SETTINGS_DRIVER_DISCOUNTS} element={<DriverDiscounts />} />
                    <Route path={ROUTES.SETTINGS_VEHICLE_TYPES} element={<VehicleTypes />} />
                    <Route path={ROUTES.SETTINGS_FAQ} element={<Faq />} />
                    <Route path={ROUTES.SETTINGS_PAGES} element={<Pages />} />
                    <Route path={ROUTES.SETTINGS_BANK_ACCOUNTS} element={<BankAccounts />} />
                    <Route path={ROUTES.COMMUNICATION_NOTIFICATIONS} element={<NotificationSend />} />
                    <Route path={ROUTES.COMMUNICATION_BULK_SMS} element={<BulkSms />} />
                    <Route path={ROUTES.COMMUNICATION_CONTACT} element={<Navigate to={ROUTES.COMMUNICATION_BULK_SMS} replace />} />
                    <Route path={ROUTES.PAYMENTS_BANK_TRANSFER} element={<BankTransferPayments />} />
                    <Route path={ROUTES.USERS} element={<Users />} />
                    <Route path={ROUTES.TRIPS} element={<Trips />} />
                    <Route path={ROUTES.TRIP_DETAIL} element={<TripDetail />} />
                    <Route path={ROUTES.TRIP_LOGS} element={<LiveRides />} />
                    <Route path={ROUTES.DRIVERS} element={<Drivers />} />
                    <Route path={ROUTES.COMMUNICATION_CHAT} element={<><ChatApp /><SignalRMonitor /></>} />
                    <Route path={ROUTES.PROFILE} element={<Profile />} />
                    <Route path={ROUTES.USER_DETAIL} element={<UserDetail />} />
                    <Route path={ROUTES.SETTINGS_SYSTEM_LOGS_SMS} element={<SmsLogs />} />
                    <Route path={ROUTES.LOCATION_ACTIVE_DRIVERS} element={<ActiveDrivers />} />
                    <Route path={ROUTES.AUTHORIZED_USERS} element={<AuthorizedUsers />} />
                    <Route path={ROUTES.SETTINGS_AGREEMENTS} element={<Agreements />} />
                    <Route path={ROUTES.SUBSCRIPTIONS_DRIVERS} element={<DriverSubscriptions />} />
                    <Route path={ROUTES.SUBSCRIPTIONS_AUDIT_LOGS} element={<SubscriptionAuditLogs />} />
                    <Route path={ROUTES.SUBSCRIPTIONS_STATISTICS} element={<SubscriptionStatistics />} />
                    <Route path={ROUTES.CRM} element={<Crm />} />
                    <Route path={ROUTES.CUSTOMER_MESSAGES} element={<CustomerMessages />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS} element={<Navigate to={ROUTES.OPERATIONAL_METRICS_OVERVIEW} replace />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS_OVERVIEW} element={<MetricsOverview />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS_DRIVERS} element={<DriverMetrics />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS_PASSENGERS} element={<PassengerMetrics />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS_PERFORMANCE} element={<PerformanceReport />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS_COVERAGE} element={<CoverageHeatmap />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS_ACCEPTANCE_CANCELLATION} element={<AcceptanceCancellationReport />} />
                    <Route path={ROUTES.OPERATIONAL_METRICS_DRIVER_PENALTIES} element={<DriverPenalties />} />
                    <Route path="/signalr-test" element={<SignalRTest />} />
                    <Route path="/socket-service-test" element={<SocketServiceTest />} />
                    <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
                  </Route>

                  {/* Harita layout - SideBar var, Footer yok */}
                  <Route element={<MapLayout />}>
                    <Route path={ROUTES.LOCATION_SMART_MAP} element={<><SmartMap /><SignalRMonitor /></>} />
                  </Route>

                  {/* Mobil layout */}
                  <Route element={<MobileRouteLayout />}>
                    <Route path={ROUTES.MOBILE} element={<MobileDashboard />} />
                    <Route path={ROUTES.MOBILE_SMART_MAP} element={<MobileSmartMap />} />
                    <Route path={ROUTES.MOBILE_USERS} element={<MobileUsers />} />
                    <Route path={ROUTES.MOBILE_DRIVERS} element={<MobileDrivers />} />
                    <Route path={ROUTES.MOBILE_TRIPS} element={<MobileTrips />} />
                    <Route path={ROUTES.MOBILE_LIVE_RIDES} element={<MobileLiveRides />} />
                    <Route path={ROUTES.MOBILE_NOTIFICATIONS} element={<MobileNotifications />} />
                    <Route path={ROUTES.MOBILE_METRICS_OVERVIEW} element={<MobileMetricsOverview />} />
                    <Route path={ROUTES.MOBILE_METRICS_DRIVERS} element={<MobileDriverMetrics />} />
                    <Route path={ROUTES.MOBILE_METRICS_PASSENGERS} element={<MobilePassengerMetrics />} />
                    <Route path={ROUTES.MOBILE_METRICS_PERFORMANCE} element={<MobilePerformanceReport />} />
                    <Route path={ROUTES.MOBILE_METRICS_COVERAGE} element={<MobileCoverageHeatmap />} />
                    <Route path={ROUTES.MOBILE_METRICS_ACCEPTANCE_CANCELLATION} element={<MobileAcceptanceCancellationReport />} />
                  </Route>

                  {/* Root redirect */}
                  <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                </Route>
              </Routes>
            </ConfigProvider>
          </LocationListener>
        </SignalRProvider>
      </AppInitializer>
    </BrowserRouter>
  );
}

export default App;
